<template>
  <section ref="kv-nested-table" class="kv-tree-table-form">
    <Row :gutter="20" class="table-wrap">
      <Row class="thead">
        <i-col :span="fieldColSpan" class="ityiem-col">
          {{ $t('common.key') }}
        </i-col>
        <i-col :span="fieldColSpan" class="item-col">
          {{ $t('common.value') }}
        </i-col>
        <i-col v-if="operable" :span="operableColSpan" class="item-col">
          {{ $t('common.operations') }}
        </i-col>
      </Row>
      <TableTreeItem
        ref="tableTreeRef"
        v-for="(item, index) in formData.list"
        :key="index"
        :index="index"
        :item="item"
        :operable="operable"
        :fieldColSpan="fieldColSpan"
        :operableColSpan="operableColSpan"
        @addItem="addItem"
        @removeItem="removeItem"
      />
    </Row>
    <Row v-if="operable" :gutter="20" class="footer">
      <Button type="text" class="btn-add" icon="md-add" ghost @click="addPrimaryItem">
        {{ $t('common.add') }}
      </Button>
    </Row>
  </section>
</template>

<script>
import { cloneDeep } from 'lodash'
import { dataType } from '@/utils/handleData'
import TableTreeItem from './TableTreeItem.vue'

export default {
  name: 'KeyValueTableTreeEditor',
  components: { TableTreeItem },
  model: { prop: 'value', event: 'update' },
  props: {
    // The submit data format is consistent with the type of `value`
    value: { type: [Array, Object], default: () => {} },
    properties: { type: [Array, Object], default: () => null },
    operable: { type: Boolean, default: true },
    submitDataType: { type: String, default: 'list' }, // list || object
  },

  data() {
    return {
      formData: {
        list: [],
      },
    }
  },

  computed: {
    operableColSpan() {
      return this.operable ? '4' : '0'
    },
    fieldColSpan() {
      return this.operable ? '10' : '12'
    },
    valueType() {
      return dataType(this.value)
    },
    submitedDataType() {
      return this.submitDataType
    },
    propertiesList() {
      const data = this.properties
      if (!data) return null

      let list = []
      if (dataType(data) === 'array') {
        list = data
      } else if (dataType(data) === 'object') {
        list = this.parsePropertiesJSONToList(data)
      }
      return list
    },
    listObjectType() {
      return ['list_object', 'object']
    },
  },

  watch: {
    value: {
      immediate: true,
      async handler(newV) {
        await this.initTableData(newV, this.propertiesList)
      },
      deep: true,
    },
    properties: {
      immediate: true,
      async handler(newV) {
        if (newV) {
          this.initTableData(this.value, this.propertiesList)
        }
      },
      deep: true,
    },
  },

  methods: {
    parsePropertiesJSONToList(objectData) {
      if (!objectData || dataType(objectData) !== 'object') return []

      const list = []
      const primaryKeys = Object.keys(objectData)
      primaryKeys.forEach((pKey) => {
        const pKeyValue = objectData[pKey]
        const { default: defaultValue } = pKeyValue
        if (dataType(defaultValue) === 'object' && pKey !== 'headers') {
          const newItem = {
            ...pKeyValue,
            default: this.parsePropertiesJSONToList(defaultValue),
          }
          list.push(newItem)
        } else if (pKey === 'headers') {
          let headerDefaultValue = defaultValue || {}
          if (dataType(headerDefaultValue) === 'object') {
            headerDefaultValue = Object.values(headerDefaultValue)
          }
          const newItem = {
            ...pKeyValue,
            default: headerDefaultValue || [],
          }
          list.push(newItem)
        } else {
          list.push(pKeyValue)
        }
      })
      return list
    },
    async initTableData(value, properties) {
      // List to 'JSON'
      if (this.valueType === 'array') {
        if (properties) {
          let list = properties
          // edit
          if (value && value.length) {
            const flatMapValue = this.flatMapArrayValue(value)
            list = this.mergeArrayValueIntoProperties(properties, flatMapValue)
          }
          this.formData.list = await this.parseArrayValue(list)
          return
        }

        // if donot exit `properties`, directly use the value
        const list = value || []
        this.formData.list = await this.parseArrayValue(list)
        return
      }

      // 'JSON' to List
      if (this.valueType === 'object') {
        if (properties) {
          let list = properties

          // edit
          if (value && Object.keys(value).length) {
            const JSONTolist = this.parseJSONValueToList(this.value)
            const flatMapValue = this.flatMapArrayValue(JSONTolist)
            list = this.mergeArrayValueIntoProperties(properties, flatMapValue)
          }

          this.formData.list = await this.parseArrayValue(list)
          return
        }

        // if donot exit `properties`, directly use the value
        const list = this.parseJSONValueToList(value)
        this.formData.list = await this.parseArrayValue(list)
      }
    },

    // list value: Tiled values, used when 'editing' or 'properties' are available
    flatMapArrayValue(arr) {
      let list = []
      if (Array.isArray(arr)) {
        list = arr.flatMap((item) => {
          const { name, default: defaultValue, type } = item
          if (dataType(defaultValue) === 'array' && this.listObjectType.includes(type) && name !== 'headers') {
            const newDefaultValue = (defaultValue || []).map((d) => {
              const { name: dName } = d
              // Use 'index' because duplicate names will appear at the same time，the same with function mergeArrayValueIntoProperties
              const parentWithIndex = `${name}-${dName}`
              return { ...d, parentWithIndex }
            })
            return this.flatMapArrayValue(newDefaultValue)
          }
          return item
        })
      }
      return list
    },
    // list value: Merge the values into the properties When 'editing' or 'properties' are available
    mergeArrayValueIntoProperties(properties, value) {
      const data = []
      properties.forEach((item) => {
        const { name, type, default: defaultValue, parentWithIndex } = item
        let newItem = {
          ...item,
        }

        if (this.listObjectType.includes(type) && name !== 'headers') {
          const newDefaultValue = defaultValue.map((d) => {
            const { name: dName } = d
            // the same with `flatMapArrayValue` function
            const parent = `${name}-${dName}`
            return {
              ...d,
              parentWithIndex: parent,
            }
          })
          newItem = {
            ...newItem,
            default: this.mergeArrayValueIntoProperties(newDefaultValue, value),
          }
        } else {
          let fValue = null
          if (parentWithIndex) {
            fValue = value.find((eitem) => eitem.parentWithIndex === parentWithIndex)
          } else {
            fValue = value.find((eitem) => eitem.name === name)
          }
          newItem = {
            ...newItem,
            default: fValue?.default || defaultValue,
          }
        }

        data.push(newItem)
      })
      return data
    },

    // init data: parse list value
    parseArrayValue(data, fname) {
      const list = []
      if (data && Array.isArray(data) && data.length) {
        const $value = cloneDeep(data)
        if (dataType($value) === 'array') {
          // 1. data is `array`
          $value.forEach(async (item, index) => {
            const { id, type, name: itemName, default: defaultValue, children, showChildren, control, optional } = item

            const isShowChildren = showChildren || false
            const newId = id || fname ? `${fname}${itemName}${index}` : `${itemName}`

            // 1.1 array item type is object
            if (dataType(item) === 'object') {
              let newItem = {
                id: newId,
                name: itemName || '',
                default: defaultValue || '',
                type: type || 'string',
                children: [],
                showChildren: isShowChildren,
                control,
                optional,
              }

              // if array item include nested `children` or `default` type is `list_object`
              if (children?.length || (this.listObjectType.includes(type) && itemName !== 'headers')) {
                const arr = children?.length ? children : defaultValue
                newItem = {
                  ...newItem,
                  children: await this.parseArrayValue(arr, itemName),
                }
              }

              // headers
              if (this.listObjectType.includes(type) && itemName === 'headers') {
                const defaultValueType = dataType(defaultValue)
                let newDefaultValue = defaultValue || []
                if (defaultValueType !== 'array' && defaultValueType !== 'object') {
                  newDefaultValue = []
                }

                newItem = {
                  ...newItem,
                  default: newDefaultValue,
                }
              }

              list.push(newItem)
            } else {
              // 1.2 array item type is `string` or `number`
              list.push({
                id: newId,
                name: index,
                default: item || '',
                type,
                showChildren: isShowChildren,
                children: children || [],
                control,
                optional,
              })
            }
          })
        }
      }
      return Promise.resolve(list)
    },

    // init data: parse object value to list
    parseJSONValueToList(objectData) {
      if (!objectData || dataType(objectData) !== 'object') return []

      const list = []
      const primaryKeys = Object.keys(objectData)
      primaryKeys.forEach((pKey) => {
        const pKeyValue = objectData[pKey]
        if (dataType(pKeyValue) === 'object' && pKey !== 'headers') {
          const newItem = {
            name: pKey,
            type: 'list_object',
            default: this.parseJSONValueToList(pKeyValue),
          }
          list.push(newItem)
        } else if (pKey === 'headers') {
          let headersValue = pKeyValue
          if (dataType(pKeyValue) === 'object') {
            headersValue = this.handlerHeadersFormat(pKeyValue, 'list')
          }
          const newItem = {
            name: pKey,
            default: headersValue || [],
            type: 'list_object',
          }
          list.push(newItem)
        } else {
          const newItem = {
            name: pKey,
            default: pKeyValue,
            type: dataType(pKeyValue),
          }
          list.push(newItem)
        }
      })
      return list
    },

    // operation data
    findChildren(childrenId, list, callback) {
      for (let i = 0; i < list.length; i += 1) {
        const item = list[i]
        if (item.id === childrenId) {
          callback(i, item, list)
        } else if (item.children) {
          this.findChildren(childrenId, item.children, callback)
        }
      }
    },

    removeItem(row) {
      this.findChildren(row.id, this.formData.list, (index, item, list) => {
        list.splice(index, 1)
      })
    },

    addItem(row) {
      this.findChildren(row.id, this.formData.list, (index, item) => {
        this.$set(row, 'showChildren', true)
        const len = item.children.length
        const newID = `${row.id}${len}`
        const newChildrenItem = {
          id: newID,
          name: '',
          default: '',
          type: 'string',
          children: [],
          control: 'text',
        }
        item.children.push(newChildrenItem)
      })
    },

    addPrimaryItem() {
      const len = this.formData.list.length

      const newPrimaryItem = {
        id: `newPrimaryItem${len}`,
        name: '',
        default: '',
        type: 'string',
        children: [],
        control: 'text',
      }
      this.formData.list.push(newPrimaryItem)
    },

    async transferKeyValueEditorData() {
      const transferDataRequests = []
      const tableTreeRefs = this.$refs.tableTreeRef
      tableTreeRefs.forEach((item) => {
        const { transferKeyValueEditorData } = item
        if (transferKeyValueEditorData) {
          transferDataRequests.push(transferKeyValueEditorData())
        }
      })
      await Promise.allSettled(transferDataRequests)
      return Promise.resolve()
    },

    // validate list
    validateTable(list, res) {
      const listData = cloneDeep(list) || cloneDeep(this.formData.list)

      if (!listData.length) {
        return []
      }

      const validateRes = res || []
      const len = listData.length
      for (let i = 0; i < len; i += 1) {
        const item = listData[i]
        const { id, name, default: defaultValue, children, errorNameMsg, errorValueMsg, optional } = item
        const childrenLen = children?.length

        if (!childrenLen) {
          if (errorNameMsg || name === '') {
            item.errorNameMsg = 'error'
            this.findChildren(id, this.formData.list, (index, nitem, nlist) => {
              this.$set(nlist, index, item)
            })
            validateRes.push(id)
          }
          if (errorValueMsg || (optional === false && defaultValue === '')) {
            item.errorValueMsg = 'error'
            this.findChildren(id, this.formData.list, (index, nitem, nlist) => {
              this.$set(nlist, index, item)
            })
            validateRes.push(id)
          }
        } else {
          this.validateTable(children, validateRes)
        }
      }
      return Promise.resolve(validateRes)
    },
    async validate() {
      const validators = [this.validateTable(this.formData.list)]

      // validate KeyValueEditors
      const tableTreeRefs = this.$refs.tableTreeRef
      tableTreeRefs.forEach((item) => {
        const { validateKeyValueEditors } = item
        if (validateKeyValueEditors) {
          validators.push(validateKeyValueEditors())
        }
      })

      const validRes = await Promise.allSettled(validators)
      const valid = validRes.every((item) => {
        const { value } = item
        if (value && Array.isArray(value)) {
          return !value.length
        }
        return value
      })

      if (valid) {
        await this.transferKeyValueEditorData()
      } else {
        const msg = '[name] is validate error'
        return Promise.reject(msg)
      }

      return Promise.resolve(true)
    },

    // Headers data reversal, for example, the `headers` data format is 'object', transfer data to 'array'
    handlerHeadersFormat(data, format) {
      if (format === 'object' && !data) return {}
      if (format.includes('list') && !data) return []

      const dataFormat = dataType(data)
      // JSON to List
      if (format.includes('list')) {
        let list = cloneDeep(data)

        if (dataFormat === 'object') {
          const keys = Object.keys(data)
          if (!keys.length) return []

          list = keys.map((k) => {
            const kValue = data[k]
            const newItem = {
              name: k,
              default: kValue || '',
              type: dataType(kValue),
            }
            return newItem
          })
        }
        return list
      }

      // List to JSON
      if (format === 'object') {
        let JSONData = cloneDeep(data)

        if (dataFormat === 'array') {
          if (!data.length) return {}

          const resData = {}
          data.forEach((item) => {
            const { name, default: defaultValue } = item
            resData[name] = defaultValue || ''
          })
          JSONData = resData
        }
        return JSONData
      }

      return {}
    },

    // transfer data: transfer list data to new list, i.e. [{name, type, default: [{name, type, default: ''}]}]
    tranaferArrayDataToList(list) {
      if (!list || !list.length) return []

      const listData = cloneDeep(list)

      const newList = []
      for (let i = 0; i < listData.length; i += 1) {
        const child = listData[i]
        const { name, default: defaultValue, children, type } = child

        let newItem = {
          name,
          default: defaultValue,
          type,
        }

        if (children?.length) {
          newItem = {
            ...newItem,
            default: this.tranaferArrayDataToList(children),
          }
        }

        if (
          (defaultValue !== '' && defaultValue !== undefined && defaultValue !== null) ||
          (dataType(defaultValue) === 'array' && defaultValue.length)
        ) {
          newList.push(newItem)
        }
      }
      return newList
    },
    // transfer data: transfer list data to json: { access: {url: "xxx", body:"xxx", expireation:"xxx" }, refresh:{ url:"xxx" } }
    transferArrayDataTOJSON(list) {
      if (!list || !Array.isArray(list) || !list.length) return {}

      const listData = cloneDeep(list)
      const dataJSON = {}

      for (let i = 0; i < listData.length; i += 1) {
        const child = listData[i]
        const { name, default: defaultValue, children, type } = child

        if (children?.length) {
          dataJSON[name] = this.transferArrayDataTOJSON(children)
        } else if (name === 'headers' && this.listObjectType.includes(type)) {
          const newHeaderDefaultValue = this.handlerHeadersFormat(defaultValue, type)
          dataJSON[name] = newHeaderDefaultValue
        } else {
          dataJSON[name] = defaultValue
        }
      }
      return dataJSON
    },

    // if you use `keyValueTreeEditor` component, data transfer must be triggered, to avoid errors when validate with other forms
    async transferData() {
      let resData = null

      if (this.submitedDataType.includes('list')) {
        resData = this.tranaferArrayDataToList(this.formData.list)
      }

      if (this.submitedDataType === 'object') {
        resData = this.transferArrayDataTOJSON(this.formData.list)
      }

      this.$emit('update', resData)
      return Promise.resolve(resData)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/mixins.scss';

.kv-tree-table-form {
  padding: 16px;
}
.table-wrap {
  overflow: hidden;
}
.thead {
  width: 100%;
  border-top: 1px solid;
  border-left: 1px solid;
  border-right: 1px solid;
  border-color: var(--color-header-bg);
  border-radius: 8px 8px 0 0;
  background-color: var(--color-header-bg);
  color: var(--color-text-normal);
  padding: 0 10px;
}
.thead .item-col {
  @include display-flex(flex-start, center);
  height: 50px;
  padding: 0 10px;
  font-weight: 700;
  color: var(--color-text-normal);
}

.btn-add {
  padding: 0;
}
.btn-add,
.btn-add:hover {
  color: #34c388;
}
</style>
